import axios from 'axios';
import CryptoJS from "crypto-js";
import Swal from 'sweetalert2';
import { processChatbotQuery } from '../utils/chatbotKnowledgeBase';

const isNativeApp = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
const DEFAULT_API = (isNativeApp || process.env.NODE_ENV === 'production') 
  ? 'https://api.bookmyaudition.com/api' 
  : 'http://localhost:5001/api';

const API_URL = process.env.REACT_APP_API_URL || DEFAULT_API;
const PUBLIC_KEY = 'eyJpZCI6InN5c3RlbV9qb2dldF91c2VyIiwiam9nZXRfdXNlcl9pZCI6ImFub25fc3lzdGVtX3VzZXJfdXVpZCIsInJvbGUiOiJhbm9ueW1vdXMiLCJpYXQiOjE3MDY3NzQ0MDAsImV4cCI6MTcwNjc3ODAwMH0.53d955944702473ba5e682498148c0b5'; // Use process.env.REACT_APP_PUBLIC_API_KEY in a real app

const api = axios.create({ 
    baseURL: API_URL,
    headers: {
        "Content-Type": "application/json",
        'x-api-key': PUBLIC_KEY 
    }
});

const getToken = () => localStorage.getItem("authToken");


const SECRET = process.env.REACT_APP_CRYPTO_SECRET || "1236b67de3dd9fc0b234e3202bb302c524e5081366da52bf843ef4d88f001134";
const key = CryptoJS.SHA256(SECRET);
const iv = CryptoJS.enc.Hex.parse("00000000000000000000000000000000");

// 🔐 AES ENCRYPTION (Aligned with Backend Decryptor)
const encrypt = (data) => {
  const encrypted = CryptoJS.AES.encrypt(
    JSON.stringify(data),
    key,
    { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
  );
  return { 
    data: encrypted.toString()
  };
};

const decrypt = (payload) => {
  if (!payload) return payload;
  const targetData = typeof payload === 'string' 
    ? payload 
    : (payload.data?.data || payload.data || payload.encryptedData);

  if (!targetData || typeof targetData !== 'string') return payload;

  const decrypted = CryptoJS.AES.decrypt(
    targetData,
    key,
    { iv, mode: CryptoJS.mode.CBC, padding: CryptoJS.pad.Pkcs7 }
  );
  return decrypted.toString(CryptoJS.enc.Utf8);
};

api.interceptors.request.use((config) => {
  const userToken = localStorage.getItem("authToken");
  const adminToken = localStorage.getItem("adminToken");

  const isValidToken = (t) => t && t !== "undefined" && t !== "null" && typeof t === 'string' && t.trim() !== "";

  // Use appropriate token based on route context
  if (config.url?.includes("/admin") || window.location.pathname.startsWith("/admin")) {
    if (isValidToken(adminToken)) {
      config.headers.Authorization = `Bearer ${adminToken}`;
    }
  } else if (isValidToken(userToken)) {
    config.headers.Authorization = `Bearer ${userToken}`;
  }

  // Skip GET requests from payload encryption
  if (config.method?.toLowerCase() === "get") {
    return config;
  }

  // Skip encryption / handle binary files safely for FormData
  if (config.data instanceof FormData) {
    const oldFormData = config.data;
    const jsonData = {};
    const filesList = [];

    for (const [k, value] of oldFormData.entries()) {
      if (value instanceof File || value instanceof Blob) {
        filesList.push({ field: k, file: value });
      } else {
        jsonData[k] = value;
      }
    }

    if (Object.keys(jsonData).length > 0) {
      const encryptedPayload = encrypt(jsonData);
      const newFormData = new FormData();
      newFormData.append("encryptedData", JSON.stringify(encryptedPayload));
      filesList.forEach(({ field, file }) => {
        newFormData.append(field, file);
      });
      config.data = newFormData;
    }
    return config;
  }

  // Encrypt standard JSON payload
  if (config.data) {
    const encryptedPayload = encrypt(config.data);
    config.data = { encryptedData: encryptedPayload };
    delete config.headers["x-encrypted-data"];
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    try {
      if (response.data?.encrypted === true && response.data?.data) {
        const decrypted = decrypt(response.data);
        response.data = JSON.parse(decrypted);
      }
    } catch (err) {
      console.error("❌ Response Decryption Error:", err.message);
    }
    return response;
  },
  (error) => {
    const errorMsg = error.response?.data?.message || "Communication error with server. Please try again.";
    Swal.fire({
      icon: 'error',
      title: 'API Request Error',
      text: errorMsg,
      toast: true,
      position: 'top-end',
      showConfirmButton: false,
      timer: 4000
    });
    return Promise.reject(error);
  }
);





// --- PUBLIC / BANNER SERVICES ---
export const fetchHeroBannersApi = async () => {
    try {
        // Calls http://localhost:5001/api/lookups/banners/hero
        const response = await api.get('/lookups/banners/hero');
        return response.data;
    } catch (error) {
        console.error("Error fetching hero banners from backend:", error.message);
        throw error;
    }
};
export const fetchAuditionsApi = (category) => api.get(`/auditions${category ? `?category=${category}` : ''}`);
export const fetchAuditionDetailsApi = (slugOrId) => api.get(`/auditions/${slugOrId}`);
// export const fetchLookupDataApi = (groupName) => api.get(`/lookups/${encodeURIComponent(groupName)}`);

// --- AUTH SERVICES (PASSWORD & OTP) ---
export const loginWithPasswordApi = (credentials) => api.post('/auth/login/password', credentials);
export const initiateRegistrationApi = (userData) => api.post('/auth/register/initiate', userData);
export const verifyRegistrationOtpApi = (otpData) => api.post('/auth/register/verify', otpData);
export const requestOtpApi = (mobileData) => api.post('/auth/request-otp', mobileData);
export const loginWithOtpApi = (otpData) => api.post('/auth/login/otp', otpData);
export const sendOtpApi = (userData) => api.post('/auth/auth/send-otp', userData);
export const verifyOtpApi = (userData) => api.post('/auth/auth/verify-otp', userData);
export const registerWithOtpApi = (userData) => api.post('/auth/auth/register-with-otp', userData);
export const registerUserApi = (userData) => api.post('/auth/register', userData);
export const fetchUserProfileApi = () => api.get('/auth/me');

// --- USER PROFILE SERVICES ---
export const updateUserProfileApi = (profileData) => api.put('/users/profile', profileData);
export const fetchJogetUserProfileApi = (localUserId) => api.get(`/users/profile/joget/${localUserId}`);
export const saveOrUpdateJogetUserProfileApi = (profileData) => api.post('/users/profile/joget', profileData);
export const fetchCdDirJogetProfileApi = (localUserId) => api.get(`/cddir/profile/cd-dir/${localUserId}`);
export const saveOrUpdateCdDirJogetProfileApi = (profileData) => api.post('/cddir/profile/cd-dir', profileData);
// export const fetchUserJogetProfileApi = (localUserId) => api.get(`/users/profile/user/${localUserId}`);
// export const fetchUserJogetProfileApi = (localUserId) => {

//     if (!localUserId || localUserId === "add") {
//         return Promise.resolve({ data: { jogetData: null } });
//     }

//     return api.get(`/users/profile/user/${localUserId}`);
// };
export const fetchUserJogetProfileApi = (localUserId) => {
    if (!localUserId || localUserId === "add") {
        return Promise.resolve({ data: { jogetData: null } });
    }
    if (typeof localUserId === 'string' && localUserId.includes('-')) {
        return api.get(`/users/profileByUUID/${localUserId}`);
    }
    return api.get(`/users/profile/user/${localUserId}`);
};
export const saveOrUpdateUserProfileApi = (profileData) => api.post('/users/profile/user', profileData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});

// --- PROJECT SERVICES ---
export const fetchProjectListApi = () => api.get('/projects/list');
export const fetchProjectsByUserApi = () => api.get('/projects/list'); // New API for Role Page selection
export const fetchProjectByIdApi = (jogetProjectId) => api.get(`/projects/${jogetProjectId}`);
export const fetchDirectorListApi = () => api.get('/projects/directors');
export const createProjectApi = (formData) => api.post('/projects/create', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const updateProjectApi = (formData) => api.post('/projects/update', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const deleteProjectApi = (jogetProjectId) => api.delete(`/projects/${jogetProjectId}`);

// --- ROLE SERVICES ---
export const fetchRoleListApi = () => api.get('/roles/list');
export const fetchPublicRoleListApi = () => api.get('/roles/public/list');
export const fetchRoleByIdApi = (localRoleId) => api.get(`/roles/${localRoleId}`);
export const fetchRoleListByIdApi = (jogetRoleId) => api.get(`/roles/r/${jogetRoleId}`);
export const fetchRoleDetailsPublicByIdApi = (jogetRoleId, urlSlug) => api.get(`/roles/public/${jogetRoleId}/${urlSlug}`); // New API for Audition Detail Page
export const fetchRoleSchedulePublicByIdApi = (jogetRoleId, audition_Type) => api.get(`/roles/public/schedule/${jogetRoleId}/${audition_Type}`);
export const fetchRoleSceneScriptByIdApi = (jogetRoleId) => api.get(`/roles/sceneScript/${jogetRoleId}`);
export const fetchRoleCheckApplicationByIdApi = (jogetRoleId) => api.get(`/roles/check-application/${jogetRoleId}`);
export const saveOrUpdateRoleApi = (payload) => api.post('/roles/saveOrUpdate', payload);
export const deleteRoleApi = (localRoleId) => api.delete(`/roles/${localRoleId}`);

// --- APPLICATION & PAYMENT SERVICES ---
export const applyToRoleApi = (payload) => api.post('/roles/apply', payload);
export const saveApplicationReview = (payload) => api.post('/roles/save-application-review', payload);
export const getAIAnalysisApi = (applicationId) => api.get(`/ai/${applicationId}`);
export const updateApplicationPaymentStatusApi = (payload) => api.post('/roles/update-payment-status', payload);
export const fetchAppliedRoleListApi = () => api.get('/roles/applied-list');
export const getAppliedRoleListForDrCr = () => api.get('/roles/list-for-dr-cr');
export const fetchCurrentAuditionDetailsApi = (jogetApplicationId) => api.get(`/roles/d/application-details/${jogetApplicationId}`);
export const modifyAdminCommentApi = (payload) =>  api.post('/roles/modify-comment', payload);
// export const fetchNotificationsApi = () => api.get('/roles/my-notifications');
export const fetchNotificationsApi = () => api.get('/roles/my-notifications');

// export const uploadAuditionVideoApi = (formData, config = {}) => api.post('/roles/upload-audition-video', formData, config);
export const uploadAuditionVideoApi = (formData, config = {}) => {
  return api.post(
    "/roles/upload-audition-video",
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data"
      },
      ...config
    }
  );
};

// --- PAYMENT GATEWAY SERVICES ---
export const createPaymentOrderApi = (paymentData) => api.post('/payment/create-order', paymentData);
export const verifyPaymentApi = (verificationData) => api.post('/payment/verify', verificationData);
export const fetchTemplatesApi = () => api.get('/admin/templates');
export const saveTemplateApi = (data) => api.post('/admin/templates/save', data);
export const deleteTemplateApi = (id) => api.delete(`/admin/templates/${id}`);

export const submitContactFormApi = (formData) => api.post('/contact/submit', formData);

//  --- COST PAY ---

export const calculateCostApi = (data) => api.post('/cost/calculate', data); 
export const redeemCoupon = (data) =>
    api.post('/coupons/redeem', data);


export const confirmCouponUsage = async (data) => {

    const response = await api.post(
        '/coupons/confirm-coupon-usage',
        data
    );

    return response.data;
};
export const fetchActiveCouponsApi = () => api.get('/coupons/public-active-coupons');
export const fetchTestimonialsApi = () =>
  api.get('/testimonials/public');
export const fetchCastingDirectorsApi = () =>
  api.get('/testimonials/casting-directors');

// ======================================
// FEEDBACK API
// ======================================

export const createPlatformFeedbackApi = (data) => api.post('/feedback/platform/create',data);
export const createDirectorFeedbackApi = (data) => api.post('/feedback/director/create',data);
export const fetchFeedbackDirectorsApi =() =>api.get('/feedback/directors');
export const fetchDirectorTestimonialsApi = () =>api.get('/feedback/director/list');
export const fetchPublicDirectorStatsApi =(id) =>api.get(`/public/director/${id}/stats` );
export const fetchDirectorProfileApi = (id) =>  api.get(`/public/director-profile/${id}`);
export const fetchTalentDashboardApi = () => api.get('/talent-dashboard');
export const fetchCastingDirectorDashboardApi = () => api.get('/casting-director-dashboard');
export const fetchTalentPaymentsApi = () => api.get('/auditions/talent/payments');
export const fetchCastingDirectorPaymentsApi = () => api.get('/auditions/casting-director-payments');
export const fetchPublicAppliedRoleListApi = (id) =>
  api.get(`/public/director/${id}/applied-list`);
// Add this inside services/api.js
export const fetchPublicDirectorProfileApi = (id) => api.get(`/public-profile/${id}`);
export const forgotPasswordOtpApi = (data) => api.post("/auth/forgot-password/send-otp", data);
export const resetPasswordApi = (data) => api.post('/auth/reset-password', data);
export const verifyForgotPasswordOtpApi = (data) =>  api.post("/auth/verify-forgot-password-otp", data);

export const getActiveGatewayApi =() =>api.get('/payment/active-gateway');
export const getMyMeetingsApi = () => api.get("/meetings/my-meetings");
export const joinMeetingApi = (applicationId) => api.get(`/meetings/join/${applicationId}`);
export const incrementKnowMoreApi = (userId) => api.post(`/users/profile/know-more/${userId}`);
// --- AI AUDITION ANALYSIS SERVICES ---
export const generateAiAnalysisApi = (applicationId) => api.post(`/roles/generate-ai-analysis/${applicationId}`);

export const followUserApi = (followingId) => api.post('/users/follow', { followingId });
export const checkFollowStatusApi = (followingId) => api.post('/users/follow/status', { followingId }); // ✅ Changed to POST
export const removeFollowerApi = (followerId) => api.post('/casting-director-dashboard/remove-follower', { followerId });

// --- CHATBOT SERVICE WITH BACKEND API & KNOWLEDGE BASE FALLBACK ---
export const sendChatbotQueryApi = async (message, history = []) => {
  try {
    const response = await api.post('/chatbot/query', { message, history });
    if (response.data && response.data.data) {
      return response.data.data;
    }
    if (response.data && (response.data.reply || response.data.answer)) {
      return response.data;
    }
  } catch (error) {
    console.log("Backend Chatbot API offline/unreachable. Serving from Knowledge Base fallback.");
  }
  return processChatbotQuery(message);
};

export default api;